"use client";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import {
  Field,
  FieldContent,
  FieldLabel,
  FieldError,
} from "../../@/components/ui/field";
import { Textarea } from "../../@/components/ui/textarea";
import { Plus, Info } from "lucide-react";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { cn } from "@/lib/utils";
import { useR2Upload } from "../lib/useR2Upload";
import {
  buildLessonMarkdownObjectKey,
  getLessonMarkdownAllowedHosts,
  getLessonMarkdownUploadTarget,
  withLessonMarkdownCacheBust,
} from "@/lib/r2LessonMarkdownPath";

function isProbablyUrl(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(trimmed);
    return true;
  } catch {
    return false;
  }
}

function normalizeR2PublicUrlForPersistence(value) {
  if (!isProbablyUrl(value)) return null;
  try {
    const u = new URL(String(value).trim());
    // Persist a stable URL (no cache-buster query/hash).
    u.search = "";
    u.hash = "";
    return u.toString();
  } catch {
    return null;
  }
}

const addLessonSchema = z.object({
  title: z.string().min(1, "Lesson title is required"),
  order: z.preprocess((val) => {
    if (typeof val === "string") return Number(val);
    return val;
  }, z.number().min(1, "Order must be >= 1")),
  videoUrl: z.string().url().optional().or(z.literal("")),
  markdownUrl: z.string().url().optional().or(z.literal("")),
  content: z.string().optional(),
});

export function AddLessonDialog({
  topicId,
  topicOrder,
  topicTitle,
  nextOrder = 1,
  onLessonAdded,
  refetchTopics,
  courseSlug = "",
}) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const [isLoadingMarkdown, setIsLoadingMarkdown] = useState(false);
  const { getToken } = useAuth();
  const { isUploading, uploadFile } = useR2Upload();
  const form = useForm({
    resolver: zodResolver(addLessonSchema),
    defaultValues: {
      title: "",
      order: nextOrder,
      videoUrl: "",
      markdownUrl: "",
      content: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.setValue("order", nextOrder);
    }
  }, [open, nextOrder, form]);

  const activeMarkdownFetchRef = useRef(null);
  const markdownFetchSeqRef = useRef(0);

  useEffect(() => {
    if (open) return;
    if (activeMarkdownFetchRef.current) {
      activeMarkdownFetchRef.current.abort();
      activeMarkdownFetchRef.current = null;
    }
    setIsLoadingMarkdown(false);
  }, [open]);

  // Markdown editor handlers
  const handleMarkdownEditorClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownSave = (value) => {
    form.setValue("content", value, { shouldDirty: true, shouldValidate: true });
    setMarkdownEditorOpen(false);
  };

  const handleOpenMarkdownEditor = () => {
    setMarkdownEditorOpen(true);
  };

  const ensureMdExtension = (path) => {
    if (!path) return path;
    return path.toLowerCase().endsWith(".md") ? path : `${path}.md`;
  };

  const objectKeyFromPublicUrl = (value) => {
    if (!isProbablyUrl(value)) return null;
    try {
      const u = new URL(String(value).trim());
      const allowedHosts = new Set(getLessonMarkdownAllowedHosts());
      if (allowedHosts.size > 0 && !allowedHosts.has(u.host)) {
        return null;
      }

      const key = u.pathname.replace(/^\/+/, "");
      return key || null;
    } catch {
      return null;
    }
  };

  const isAllowedR2MarkdownUrl = (value) => {
    if (!isProbablyUrl(value)) return false;
    try {
      const u = new URL(String(value).trim());
      const allowedHosts = new Set(getLessonMarkdownAllowedHosts());
      return allowedHosts.size === 0 ? false : allowedHosts.has(u.host);
    } catch {
      return false;
    }
  };

  const fetchMarkdownText = async (url, signal) => {
    const cacheBustedUrl = withLessonMarkdownCacheBust(url);
    const res = await fetch(cacheBustedUrl, { signal, cache: "no-store" });
    if (!res.ok) {
      throw new Error(`Failed to fetch markdown (HTTP ${res.status})`);
    }
    return await res.text();
  };

  const watchedMarkdownUrl = form.watch("markdownUrl");
  useEffect(() => {
    if (!open) return;

    const url = typeof watchedMarkdownUrl === "string" ? watchedMarkdownUrl.trim() : "";
    if (!isProbablyUrl(url)) return;

    if (!isAllowedR2MarkdownUrl(url)) {
      toast.error("That URL is not an R2 markdown URL.");
      return;
    }

    const contentState = form.getFieldState("content");
    if (contentState?.isDirty) return;

    if (activeMarkdownFetchRef.current) {
      activeMarkdownFetchRef.current.abort();
    }
    const controller = new AbortController();
    activeMarkdownFetchRef.current = controller;
    const seq = ++markdownFetchSeqRef.current;

    setIsLoadingMarkdown(true);
    fetchMarkdownText(url, controller.signal)
      .then((text) => {
        if (markdownFetchSeqRef.current !== seq) return;
        const state = form.getFieldState("content");
        if (state?.isDirty) return;
        form.setValue("content", text ?? "", { shouldDirty: false });
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        console.error("Failed to load markdown content from URL", err);
        toast.error(
          "Could not load the markdown file content (CORS or missing file)."
        );
      })
      .finally(() => {
        if (markdownFetchSeqRef.current !== seq) return;
        setIsLoadingMarkdown(false);
      });
  }, [open, watchedMarkdownUrl, form]);

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      const token = await getToken();
      if (!token) return;

      if (!topicId) {
        toast.error("No topic selected.");
        setIsSubmitting(false);
        return;
      }

      const providedMarkdownUrl = values.markdownUrl?.trim() || "";

      if (providedMarkdownUrl && !isAllowedR2MarkdownUrl(providedMarkdownUrl)) {
        toast.error("That URL is not an R2 markdown URL.");
        return;
      }

      const contentState = form.getFieldState("content");
      const contentDirty = Boolean(contentState?.isDirty);

      let markdownText = typeof values.content === "string" ? values.content : "";
      let trimmedMarkdownText = markdownText.trim();

      // Persist a stable URL for backend fields.
      const providedStableUrl = providedMarkdownUrl
        ? normalizeR2PublicUrlForPersistence(providedMarkdownUrl)
        : null;

      // This URL is what the backend expects for both `content` and `r2_public_url`.
      let r2PublicUrl = providedStableUrl || null;
      let r2ObjectKey = r2PublicUrl ? objectKeyFromPublicUrl(r2PublicUrl) : null;

      // If there's markdown text, decide whether to upload.
      // - URL present + textarea untouched (auto-prefill): don't upload, just store URL + text.
      // - URL absent OR textarea edited: upload to R2 (overwrite same key if URL is our host).
      if (trimmedMarkdownText) {
        const shouldUpload = !r2PublicUrl || contentDirty;

        if (shouldUpload) {
          const fromUrlKey = r2PublicUrl ? objectKeyFromPublicUrl(r2PublicUrl) : null;

          const computedKey = buildLessonMarkdownObjectKey({
            courseSlug,
            topicOrder,
            topicTitle,
            topicId,
            lessonOrder: values.order,
            lessonTitle: values.title,
          });
          // If the existing URL is one of our hosts, we *could* overwrite its key.
          // We prefer the computed (standardized) key for new uploads.
          const objectKey = ensureMdExtension(computedKey || fromUrlKey);
          const fileName =
            objectKey.split("/").pop() || `lesson-${Date.now()}.md`;

          const markdownFile = new File([markdownText], fileName, {
            type: "text/markdown",
          });

          const { bucketName, publicUrlBase } = getLessonMarkdownUploadTarget();

          const uploadedUrl = await uploadFile(markdownFile, objectKey, {
            silent: true,
            bucketName,
            publicUrlBase,
            cacheControl: "no-cache, max-age=0, must-revalidate",
          });

          if (import.meta.env.DEV) {
            console.log("[r2 upload] lesson markdown", {
              courseSlug,
              topicId,
              objectKey,
              uploadedUrl,
              bucketName,
              publicUrlBase,
              overwrite: Boolean(fromUrlKey),
            });
          }

          if (!uploadedUrl) {
            toast.error("Failed to upload lesson markdown to storage.");
            return;
          }

          const stableUploadedUrl =
            normalizeR2PublicUrlForPersistence(uploadedUrl) || uploadedUrl;
          r2PublicUrl = stableUploadedUrl;
          r2ObjectKey = objectKey;
        }
      }

      const payload = {
        title: values.title,
        topic_id: topicId,
        order: Number(values.order),
        video_url: values.videoUrl || null,
        // Send multiple key variants for backend compatibility.
        r2_object_key: r2ObjectKey,
        r2ObjectKey: r2ObjectKey,
        r2_public_url: r2PublicUrl,
        r2PublicUrl: r2PublicUrl,
        markdown_url: r2PublicUrl,
        markdownUrl: r2PublicUrl,
        // Backend expects the markdown URL here (not the markdown text).
        content: r2PublicUrl || null,
      };

      console.log("Creating lesson with payload:", payload);

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        api_endpoint + "/lesson/create",
        payload,
        reqConfig
      );
      const lesson = response.data.lesson;
      console.log("Lesson created:", response.data.lesson);
      toast.success(`Added lesson "${lesson.title}" successfully!`);
      setOpen(false);
      form.reset({
        title: "",
        order: nextOrder + 1,
        videoUrl: "",
        markdownUrl: "",
        content: "",
      });
      if (onLessonAdded) onLessonAdded(lesson);
      if (refetchTopics) refetchTopics(); // Refetch topics
    } catch (error) {
      console.error("Failed to create lesson:", error);
      toast.error("Failed to add lesson. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset({
      title: "",
      order: nextOrder,
      videoUrl: "",
      markdownUrl: "",
      content: "",
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="x"
        >
          Add Lesson
          <Plus className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="h-auto sm:h-auto bg-(--surface-panel) text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">Add New Lesson</DialogTitle>
          <DialogDescription className="font-body text-(--neutral-600)">
            Create a new lesson with content and optional video.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6"
              id="add-lesson-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Lesson Title *</FieldLabel>
                      <FieldContent>
                        <Input
                          {...field}
                          placeholder="Enter lesson title"
                          className={cn(
                            "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                          )}
                        />
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
                <FormField
                  control={form.control}
                  name="order"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Order *</FieldLabel>
                      <FieldContent>
                        <Input
                          {...field}
                          type="number"
                          min="1"
                          className={cn(
                            "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) mt-1",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                          )}
                        />
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="videoUrl"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Video URL
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-heading">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="https://youtube.com/..."
                        type="url"
                        className={cn(
                          "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              />

              {/* <FormField
                control={form.control}
                name="markdownUrl"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Markdown File URL
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-heading">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="https://randomurl.md/... or any r2 markdown file URL"
                        type="url"
                        className={cn(
                          "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              /> */}

              <FormField
                control={form.control}
                name="content"
                render={({ field, fieldState }) => (
                  <Field>
                    <div className="flex items-center justify-between gap-3">
                      <FieldLabel>
                        Content
                        <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-heading">
                          <Info className="w-3 h-3" />
                          Markdown supported
                        </div>
                      </FieldLabel>
                      <Button
                        type="button"
                        onClick={handleOpenMarkdownEditor}
                        size=""
                        variant="outline"
                        className="font-body"
                      >
                        Edit in Fullscreen
                      </Button>
                    </div>
                    <FieldContent>
                      <Textarea
                        {...field}
                        rows={8}
                        disabled={isSubmitting || isUploading}
                        placeholder="Enter lesson content using Markdown"
                        className={cn(
                          "min-h-50 font-mono text-sm bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500)",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: fieldState.error.message }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />
            </form>
          </Form>
        </div>

        <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className=""
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant=""
              form="add-lesson-form"
              className=""
              disabled={isSubmitting || isUploading}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center w-full">
                  <ClipLoader size={20} color="var(--neutral-50)" />
                </span>
              ) : (
                "Add Lesson"
              )}
            </Button>
        </DialogFooter>
        {/* Markdown Editor Dialog - positioned outside DialogContent but inside Dialog */}
        <MarkdownEditorDialog
          open={markdownEditorOpen}
          initialValue={form.watch("content") || ""}
          onClose={handleMarkdownEditorClose}
          onSave={handleMarkdownSave}
        />
      </DialogContent>
    </Dialog>
  );
}
